import {
    Box,
    LinearProgress,
    Typography
} from "@mui/material";
import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import {
    useRecoilCallback,
    useRecoilValue,
    useSetRecoilState
} from "recoil";
import {
    Issue as APIIssue,
    Property as APIProperty,
    ImageSource,
    SolutionTenderingType
} from "../../../API";
import {
    getCleanUpProposalStates,
    getInitializeProposal
} from "../../../lib/design/bidding/state/v2/ProposalStates";
import {
    getCleanUpSolutionStates,
    getInitializeSolutionBySolutionId,
    issueIdToIssueMapByContextSelectorFamily,
    locationIdToLocationMapByContextSelectorFamily,
    solutionAttachmentsAtom
} from "../../../lib/design/document/state/DocumentState";
import {
    getSetUpPropertyStateCallback,
    propertyIdToPropertySelectorFamily
} from "../../../lib/property/state/PropertyRecoilState";
import {
    Attachment,
    Issue,
    Location,
    Property
} from "../../../models";

import { Auth, Storage } from "aws-amplify";
import { useSnackbar } from "notistack";
import { isMobile } from "react-device-detect";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { usePageDefaultStyle } from "../../../assets/style/usePageDefaultStyle";
import ImageVariant from "../../../lib/attachment/ImageVariant";
import { ImageEntity } from "../../../lib/dashboard/types/ImageEntity";
import { issueIdToProposalItemPriceTotalMapByContextSelectorFamily } from "../../../lib/design/bidding/state/v2/ProposalItemStates";
import { StateContext } from "../../../lib/design/document/state/StateContext";
import GraphQLPropertyDAOFactory from "../../../lib/property/dao/graphql/GraphQLPropertyDAOFactory";
import { stateContextInFocusAtom } from "../../../lib/ui/InFocusRecoilStates";
import { AttachmentType } from "../../../models";
import { TeneraPropertyDashboard } from "../../dashboard/pages/TeneraPropertyDashboard";

const propertyDAO = GraphQLPropertyDAOFactory.getInstance();

export type SolutionDashboardParams = {
    readonly propertyId: string;
    readonly solutionId: string;
};

export const SolutionDashboard = () => {
    /* Internal page states */
    const { propertyId, solutionId } = useParams<SolutionDashboardParams>();
    const property = useRecoilValue<Property | null>(propertyIdToPropertySelectorFamily(propertyId));
    const locationIdToLocationMap = useRecoilValue<Map<string, Location>>(locationIdToLocationMapByContextSelectorFamily(StateContext.SOLUTION_VIEWING));
    const issueIdToIssueMap = useRecoilValue<Map<string, Issue>>(issueIdToIssueMapByContextSelectorFamily(StateContext.SOLUTION_VIEWING));

    const setStateContextInFocus = useSetRecoilState(stateContextInFocusAtom);
    const [isPageLoading, setPageLoading] = useState<boolean>(true);
    const { pageDefaultStyle } = usePageDefaultStyle();
    const snackbar = useSnackbar();
    const issueIdToProposalItemPriceTotalMap = useRecoilValue<Map<string, number>>(issueIdToProposalItemPriceTotalMapByContextSelectorFamily(StateContext.SOLUTION_VIEWING));

    /* Dependencies */
    const initializeSolutionBySolutionId = getInitializeSolutionBySolutionId(StateContext.SOLUTION_VIEWING);
    const initializeProposal = getInitializeProposal(StateContext.SOLUTION_VIEWING);
    const cleanUpSolutionStates = getCleanUpSolutionStates(StateContext.SOLUTION_VIEWING);
    const cleanUpProposalStates = getCleanUpProposalStates(StateContext.SOLUTION_VIEWING);
    const setUpPropertyStateForSignedInUser = getSetUpPropertyStateCallback();

    const solutionAttachments = useRecoilValue<Array<Attachment>>(solutionAttachmentsAtom);

    const { data: images, isLoading: isImagesLoading } = useQuery<Array<ImageEntity>>({
        queryKey: ['images', solutionAttachments],
        queryFn: async () => {
            return await Promise.all(solutionAttachments
                // attachment type of undefined and IMAGE are both images
                .filter(attachment => attachment.type !== AttachmentType.VIDEO && attachment.key)
                .map(async attachment => {
                    const thumbnailData: any = await Storage.get(
                        `${attachment.key}${ImageVariant.THUMBNAIL}`,
                        {
                            download: true
                        },
                    ).catch((error) => {
                        return undefined;
                    });
                    const thumbnail = thumbnailData ? URL.createObjectURL(thumbnailData.Body) : undefined;
                    return {
                        key: attachment.key!,
                        parentId: attachment.parentId,
                        urls: {
                            original: await Storage.get(
                                attachment.key!
                            ),
                            thumbnail
                        },
                        source: attachment.imageSource as ImageSource
                    } satisfies ImageEntity;
                }));
        },
        enabled: solutionAttachments.length > 0
    });

    const locations = useMemo(() => {
        return Array.from(locationIdToLocationMap.values()) as any;
    }, [locationIdToLocationMap]);
    const issues = useMemo(() => {
        return Array.from(issueIdToIssueMap.values());
    }, [issueIdToIssueMap]);

    useEffect(() => {
        // default to issues list page
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('page', "issues");
        window.history.pushState({}, '', `${window.location.pathname}?${searchParams.toString()}`);
        initialize();
        return () => {
            cleanUp();
        };
    }, []);

    const setUpPropertyStateForPublicUser = useRecoilCallback(({ set }) => {
        return async (propertyId: string) => {
            const property = await propertyDAO.getById(propertyId);
            set(propertyIdToPropertySelectorFamily(propertyId), property);
        };
    }, []);

    const setUpPropertyState = useCallback(async (propertyId: string) => {
        const signedInUser = await Auth.currentAuthenticatedUser().catch(() => undefined);
        if (signedInUser) {
            setUpPropertyStateForSignedInUser(propertyId);
        } else {
            setUpPropertyStateForPublicUser(propertyId);
        }
    }, []);

    useEffect(() => {
        if (propertyId && !property) {
            setUpPropertyState(propertyId);
        }
    }, [propertyId, property]);

    const initialize = async () => {
        try {
            setStateContextInFocus(StateContext.SOLUTION_VIEWING);
            const { solutionRecord } = await initializeSolutionBySolutionId(solutionId);
            if (solutionRecord.metadata?.tenderingType !== SolutionTenderingType.TENDERED) {
                await initializeProposal(solutionRecord.id, solutionRecord.latestMinorVersion!);
            }
            setPageLoading(false);
        } catch (error) {
            console.error('Error initializing solution dashboard:', error);
            snackbar.enqueueSnackbar("Failed to load solution data", { variant: "error" });
            setPageLoading(false);
        }
    };

    const cleanUp = () => {
        cleanUpSolutionStates();
        cleanUpProposalStates();
    };

    if (isPageLoading || isImagesLoading) {
        return (
            <Box sx={pageDefaultStyle}>
                <LinearProgress />
            </Box>
        );
    }

    if (!property) {
        return (
            <Box sx={pageDefaultStyle}>
                <Typography>Property not found</Typography>
            </Box>
        );
    }

    return (
        <Box sx={pageDefaultStyle}>
            {isMobile && (<Typography variant="h2">{property.name}</Typography>)}
            {!isMobile && (
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    pt={4}
                    px={{ md: 6 }}
                >
                    <Typography variant="h2">{property.name}</Typography>
                </Box>
            )}
            <TeneraPropertyDashboard
                property={property as APIProperty}
                locations={locations}
                issues={issues as APIIssue[]}
                issueIdToProposalItemPriceTotalMap={issueIdToProposalItemPriceTotalMap}
                linkRegistries={[]}
                images={images || []}
                assessments={[]}
                assessmentSchedules={[]}
                resourceAssessmentScheduleAssociations={[]}
                tags={[]}
            />
        </Box>
    );
};